
import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Eye, EyeOff, Trash2 } from 'lucide-react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Review } from '@/lib/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CompanyReviewsListProps {
  companyName: string;
  reviews: Review[];
  onBack: () => void;
  onReviewsChange: (reviews: Review[]) => void;
}

const CompanyReviewsList = ({ 
  companyName, 
  reviews, 
  onBack,
  onReviewsChange 
}: CompanyReviewsListProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<string | null>(null);
  const [visibilityFilter, setVisibilityFilter] = useState<string>("all");

  // Toggle review visibility
  const toggleReviewVisibility = async (reviewId: string, isHidden: boolean | undefined) => {
    try {
      const newHiddenState = !isHidden;
      
      const { error } = await supabase
        .from('reviews')
        .update({ is_hidden: newHiddenState })
        .eq('id', reviewId);
        
      if (error) throw error;
      
      // Update local state
      const updatedReviews = reviews.map(review => 
        review.id === reviewId 
          ? { ...review, isHidden: newHiddenState } 
          : review
      );
      
      onReviewsChange(updatedReviews);
      
      toast({
        title: newHiddenState ? "Review hidden" : "Review made visible",
        description: `The review has been ${newHiddenState ? "hidden from" : "made visible to"} users.`,
      });
    } catch (error) {
      console.error("Error toggling review visibility:", error);
      toast({
        title: "Action failed",
        description: "Failed to update the review visibility.",
        variant: "destructive"
      });
    }
  };

  // Delete review
  const deleteReview = async () => {
    if (!reviewToDelete) return;
    
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewToDelete);
        
      if (error) throw error;
      
      // Remove from local state
      const updatedReviews = reviews.filter(review => review.id !== reviewToDelete);
      onReviewsChange(updatedReviews);
      
      toast({
        title: "Review deleted",
        description: "The review has been permanently deleted.",
      });
    } catch (error) {
      console.error("Error deleting review:", error);
      toast({
        title: "Deletion failed",
        description: "Failed to delete the review.",
        variant: "destructive"
      });
    } finally {
      setShowDeleteDialog(false);
      setReviewToDelete(null);
    }
  };

  // Handle delete button click - shows confirmation dialog
  const handleDeleteClick = (reviewId: string) => {
    setReviewToDelete(reviewId);
    setShowDeleteDialog(true);
  };

  // Filter reviews by visibility state
  const filteredReviews = reviews.filter(review => {
    if (visibilityFilter === "all") return true;
    if (visibilityFilter === "visible") return !review.isHidden;
    if (visibilityFilter === "hidden") return review.isHidden;
    return true;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-medium">Reviews for {companyName}</h2>
          <p className="text-sm text-redflags-gray">
            {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'} found
          </p>
        </div>
        <div className="flex gap-4">
          <Select 
            value={visibilityFilter}
            onValueChange={setVisibilityFilter}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reviews</SelectItem>
              <SelectItem value="visible">Visible Only</SelectItem>
              <SelectItem value="hidden">Hidden Only</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={onBack}
          >
            Back to Search
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading reviews...</div>
      ) : filteredReviews.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-redflags-gray">No reviews found with the current filter</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Experience</TableHead>
                <TableHead>Fair Work</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReviews.map((review) => (
                <TableRow key={review.id}>
                  <TableCell className="font-medium">
                    {formatDistanceToNow(review.createdAt, { addSuffix: true })}
                  </TableCell>
                  <TableCell>{review.jobTitle}</TableCell>
                  <TableCell>{review.location}</TableCell>
                  <TableCell>
                    {review.positiveExperience !== undefined && (
                      <Badge 
                        className={review.positiveExperience ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
                      >
                        {review.positiveExperience ? "Positive" : "Negative"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {review.fairWorkInvolved ? (
                      <Badge className="bg-amber-100 text-amber-800">Yes</Badge>
                    ) : (
                      <span className="text-gray-400">No</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {review.isHidden ? (
                      <Badge variant="outline" className="text-gray-500 border-gray-300">
                        Hidden
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        Visible
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleReviewVisibility(review.id, review.isHidden)}
                        title={review.isHidden ? "Make visible" : "Hide review"}
                      >
                        {review.isHidden ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteClick(review.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Delete review"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the 
              review from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteReview}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyReviewsList;
